import { http } from '@pendo/http';
import { isBetaEnabled as evaluateBeta } from '@pendo/middleware';
import { hasSegmentFlag } from '@/data/flags/local-flags';
import { getActiveSubscription } from '@/data/session/session-globals';
import { BETA_OPT_INS_SETTING, toBetaOptIns } from '@/settings/subscription-settings/betas/betas.helpers';
import { useUserSettingsStore } from '@/stores/user-settings.store';
// eslint-disable-next-line no-restricted-imports
import store from '@/vuex/store';

export function withBetaToggled (list, betaId, enabled) {
    const current = Array.isArray(list) ? list : [];
    const without = current.filter((id) => id !== betaId);

    return enabled ? [...without, betaId] : without;
}

function betaBase (subId) {
    return subId ? `/api/super/s/${subId}/beta` : '/api/s/_SID_/beta';
}

export function addUserBetaOptIn ({ userId, betaId, subId }) {
    return http.put(`${betaBase(subId)}/${betaId}/user/${userId}`, {}).then((res) => res?.data);
}

export function removeUserBetaOptIn ({ userId, betaId, subId }) {
    return http.delete(`${betaBase(subId)}/${betaId}/user/${userId}`).then((res) => res?.data);
}

export function addSubBetaOptIn ({ betaId, subId }) {
    return http.put(`${betaBase(subId)}/${betaId}`, {}).then((res) => res?.data);
}

export function removeSubBetaOptIn ({ betaId, subId }) {
    return http.delete(`${betaBase(subId)}/${betaId}`).then((res) => res?.data);
}

export function getBetaEnrolledUserIds ({ betaId, subId }) {
    return http.get(`${betaBase(subId)}/${betaId}/users`).then((res) => res?.data?.userIds ?? []);
}

export function fetchUserBetaOptIns () {
    const subId = getActiveSubscription()?.id;
    const name = subId ? `${BETA_OPT_INS_SETTING}--${subId}` : BETA_OPT_INS_SETTING;

    return http
        .get(`/api/s/_SID_/user/_UID_/setting/${name}`)
        .then((res) => toBetaOptIns(res?.data?.value))
        .catch(() => []);
}

export function fetchSubBetaOptIns (subId) {
    if (subId) {
        return http
            .get(`/api/super/s/${subId}/beta`)
            .then((res) => res?.data?.betaIds ?? [])
            .catch(() => []);
    }

    return http
        .get(`/api/s/_SID_/subscription/setting/${BETA_OPT_INS_SETTING}`)
        .then((res) => toBetaOptIns(res?.data?.value))
        .catch(() => []);
}

export async function isBetaEnabled (beta) {
    if (!beta) {
        return false;
    }

    const [userBetaOptIns, subBetaOptIns] = await Promise.all([fetchUserBetaOptIns(), fetchSubBetaOptIns()]);

    return evaluateBeta(beta, { hasSegmentFlag, userBetaOptIns, subBetaOptIns });
}

// Sync counterpart of isBetaEnabled: reads opt-ins already cached at session bootstrap instead of refetching.
// Uses the auth/hasSegmentFlag getter, not the local-flags import used elsewhere in this file: this runs
// inside Vue computeds (useSpacesBetaAccess, side-nav's shouldShowSpacesNavItem), and local-flags reads a
// module-level cache documented as non-reactive. If evaluateBeta's kill-switch check short-circuits false
// before segment flags load, a computed calling the local-flags version never tracks a dependency and
// permanently caches false. The Vuex getter reads state.segmentFlags, which is reactive.
export function hasBetaAccess (beta) {
    if (!beta) {
        return false;
    }

    const userBetaOptIns = toBetaOptIns(useUserSettingsStore().settingByName(BETA_OPT_INS_SETTING, true)?.value);
    const subBetaOptIns = toBetaOptIns(store.state.subscription.settings?.[BETA_OPT_INS_SETTING]?.value);

    return evaluateBeta(beta, { hasSegmentFlag: store.getters['auth/hasSegmentFlag'], userBetaOptIns, subBetaOptIns });
}

// Closes the load-order gap for hasBetaAccess; skips the wait if the kill switch is off (a real denial).
export async function waitForBetaOptIns (beta, { timeout = 1500 } = {}) {
    if (beta?.visibleToUsers && !hasSegmentFlag(beta.visibleToUsers)) {
        return;
    }

    const loadPromise = Promise.allSettled([
        useUserSettingsStore().loadOne({ name: BETA_OPT_INS_SETTING, namespaced: true }),
        store.dispatch('subscription/loadSetting', { setting: BETA_OPT_INS_SETTING, retrieveMetadata: false }),
    ]);

    await Promise.race([
        loadPromise,
        new Promise((resolve) => setTimeout(resolve, timeout)),
    ]);
}

export function syncBetaOptInMetadata ({ userBetaOptIns = [], subBetaOptIns = [] } = {}) {
    const betaOptIns = [...new Set([...userBetaOptIns, ...subBetaOptIns])];

    window.pendo?.updateOptions?.({ visitor: { [BETA_OPT_INS_SETTING]: betaOptIns } });
}
